// wizard.tsx — the launch WIZARD surface (centerpiece) of the Drip Campaigns
// redesign. Ported faithfully from the design handoff prototype
// (prototype/src/drip-builder.jsx + the `Wizard` shell in drip-app.jsx).
//
// ROUTE-DRIVEN (see docs/design-handoffs/drip-redesign-routing.md): the wizard
// no longer owns navigation/selection in flaky local state. Instead:
//   • The current step comes from the `/campaigns/:id/edit/:step` PATH param.
//   • Which follow-up editor is open comes from the `?step=<n>` QUERY param.
//   • The in-progress campaign (audience, name, steps, stopOnReply, timing) is a
//     persisted `Drip` with `status:'draft'`, loaded by `:id`. Every edit
//     autosaves to it (debounced patch). Reload re-hydrates from draft + URL.
// Local state holds ONLY the live working copy of the draft content (hydrated
// once from the persisted draft, autosaved back) plus transient submit spinner.
//
// The prototype's inline-style approach is kept verbatim for pixel fidelity.
// Presentation leaves come from ./primitives; icons from lucide-react.

import React from 'react';
import { useIsMobile } from '@common/hooks/use-is-mobile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { namespace } from '@common/config/constants';
import {
  X,
  Users,
  MessageSquare,
  Clock,
  CheckCircle,
  Check,
  Layers,
  Plus,
  CornerDownRight,
  Lock,
  Info,
  ArrowUp,
  ArrowDown,
  Pencil,
  Trash2,
  Reply,
  Zap,
  Calendar,
  Gauge,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Send,
  Dot,
  Repeat,
  type LucideIcon,
} from 'lucide-react';

import {
  Toggle,
  AudAvatar,
  DelayControl,
  AssetBadge,
  Eyebrow,
  gapText,
  templateToPreview,
  collapseDoubleBraces,
  type DelayGap,
  type DelayUnit,
} from './primitives';

import type { TINTS } from './primitives';
import { WIZARD_STEP_MAX, StepHeading } from './wizard-step-chrome';
import { CampaignMessageBubble, templateToPreviewMessage, isEmptyPreview } from './campaign-message-bubble';
import { StepEditor, type DripStepTemplate } from './drip-message-editor';
import { DripAudienceStep } from './drip-audience-step';
import SchedulePickerStandaloneWrapper from '@common/components/schedule-picker/schedule-picker-standalone-wrapper';
import { setDueDate, setIsRecurring, setRruleset } from '@common/reducers/ScheduledMessageEditorReducer';

import { buildSchedule, buildRandomGap, hydrateTiming, scheduledLaunchNeedsTime, CAMPAIGN_RECURRENCE_FREQUENCIES } from './campaign-timing-schedule';
import { recurrenceSummary } from './campaign-recurrence-summary';
import { describeCampaignSendSpan, describeCampaignSendRange, pacingIsWorthStating, randomGapFieldError, CAMPAIGN_SEND_SPACING_SECONDS, CAMPAIGN_RANDOM_GAP_MIN_SECONDS, CAMPAIGN_RANDOM_GAP_MAX_SECONDS, DEFAULT_RANDOM_GAP, type CampaignRandomGap, type PacingUnit } from './campaign-pacing';
import { isEmptyDraft } from './management-list';
import { LAUNCH_TIMEOUT_MS, decideLaunchOutcome, resolveLaunchOutcome } from './launch-outcome';
import { isNavigatingToSubEditor } from './nav-context';
import { boardsSelectByQuery, DBDripThunks, DBBoardsThunks, DBCampaignThunks, campaignsSelectOneObjectByQuery, DBTasksThunks, DBTaskColumnThunks, taskColumnSelectByQuery, dripsSelectOneObjectByQuery, AUDIENCE_LIST_QUERY } from '@common-reducers/DBServiceThunks';
import { resolveWizardCompletionMode, buildDripEditPatch, isDripActivelySending } from './wizard-edit-save';
import { findUnknownTokens } from '@bt-common/schemas/bt/personalization-tokens';
import { deriveAudienceLabel, intersectColumnTitles, toAudienceIds, type Translate } from './audience-selection';
import { MessageConfirmationDialog } from '@extension/context/content/components/add-scheduled-message-button/message-confirmation-dialog';
import { resolvePastTimeState } from '@extension/context/content/components/add-scheduled-message-button/past-time-gate';
import { useNowTicker } from '@extension/context/content/components/add-scheduled-message-button/use-now-ticker';
import { getTaskSumByBoardIdRecord } from '@common-reducers/CampaignSelectors';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { flowPath } from '@common/utils/clear-flow';
import { useLaunchDripMutation } from '@common/reducers/backend-api/backend-api';
import type { Drip, DripStep } from '@bt-common/schemas/bt/drip';
import type { Template } from '@bt-common/schemas/bt/campaign';
import type { Template as EditorTemplate } from '@common/models/template';
// ════════ INTERNAL UI STEP SHAPE ════════
// Moved to @common-reducers/DripEditorReducer (the new single source of truth
// for the drip working copy) — re-exported/imported here so this module and
// drip-message-editor.tsx can both use them without a circular dependency.
import {
  type UiStep,
  type TimingState,
  nextBridgeId,
  emptyTemplate,
  defaultGap,
  hydrate,
  reset,
  updateStepGap as updateStepGapAction,
  addFollowup as addFollowupAction,
  removeStep as removeStepAction,
  moveStep as moveStepAction,
  setName as setNameAction,
  setStopOnReply as setStopOnReplyAction,
  setTiming as setTimingAction,
} from '@common-reducers/DripEditorReducer';

// Does a step's template carry any sendable content?
export const hasContent = (t: DripStepTemplate): boolean =>
  !!(t.message?.trim() || t.asset?.url || t.isPoll || t.poll);

// ════════ STEPPER ════════
// Step ids — exported so the route shell can validate the `:step` path param.
export const WIZARD_STEP_IDS = ['audience', 'message', 'timing', 'review'] as const;
export type WizardStepId = (typeof WIZARD_STEP_IDS)[number];


interface StepDef {
  id: WizardStepId;
  /** i18n key; `label` is the English default rendered when the key is absent. */
  i18nKey: string;
  label: string;
  icon: LucideIcon;
}
const STEPS: StepDef[] = [
  { id: 'audience', i18nKey: 'campaignsRedesign.wizard.stepAudience', label: 'Audience', icon: Users },
  { id: 'message', i18nKey: 'campaignsRedesign.wizard.stepMessage', label: 'Message', icon: MessageSquare },
  { id: 'timing', i18nKey: 'campaignsRedesign.wizard.stepTiming', label: 'Timing', icon: Clock },
  { id: 'review', i18nKey: 'campaignsRedesign.wizard.stepReview', label: 'Review', icon: CheckCircle },
];

// The per-step label ("Message 1" / "Follow-up 1") shown on the step cards and
// on the review's message flow. A hook rather than a bare helper so both
// call sites read it off the same translation instance.
const useStepLabel = () => {
  const { t } = useTranslation(namespace);
  return React.useCallback(
    (i: number): string => (i === 0
      ? t('campaignsRedesign.wizard.messageLabel', 'Message 1')
      : t('campaignsRedesign.wizard.followupLabel', 'Follow-up {{number}}', { number: i })),
    [t],
  );
};

interface WizardStepperProps {
  active: WizardStepId;
  maxReached: number;
  onJump: (id: WizardStepId) => void;
  multi: boolean;
  stepCount: number;
}
const WizardStepper: React.FC<WizardStepperProps> = ({ active, maxReached, onJump, multi, stepCount }) => {
  const { t } = useTranslation(namespace);
  const idx = STEPS.findIndex((s) => s.id === active);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '0 4px' }}>
      {STEPS.map((s, i) => {
        const done = i < idx;
        const current = i === idx;
        const reachable = i <= maxReached;
        return (
          <React.Fragment key={s.id}>
            <button
              disabled={!reachable}
              onClick={() => reachable && onJump(s.id)}
              style={{
                appearance: 'none', border: 0, background: 'transparent', cursor: reachable ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', borderRadius: 9, opacity: reachable ? 1 : 0.5,
              }}
            >
              <span style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: current ? 'var(--bt-blue)' : done ? 'var(--success)' : 'var(--surface-3)',
                color: (current || done) ? 'var(--on-brand)' : 'var(--muted)',
                border: current || done ? '0' : '1px solid var(--line-strong)',
                fontSize: 11.5, fontWeight: 600, fontFamily: 'var(--ff-mono)', transition: 'all .15s',
              }}>
                {done ? <Check size={13} /> : i + 1}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <span style={{ fontSize: 12.5, fontWeight: current ? 600 : 500, color: current ? 'var(--ink)' : 'var(--muted)' }}>{t(s.i18nKey, s.label)}</span>
                {s.id === 'message' && multi && (
                  <span style={{ fontSize: 10, color: done ? 'var(--success)' : 'var(--bt-blue-deep)', display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Layers size={9} /> {t('campaignsRedesign.counts.messages', { count: stepCount })}
                  </span>
                )}
              </span>
            </button>
            {i < STEPS.length - 1 && (
              <div style={{ width: 28, height: 2, background: i < idx ? 'var(--success)' : 'var(--line)', borderRadius: 2, margin: '0 2px' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ════════ AUDIENCE TINT CYCLE ════════
// Real boards don't carry a tint; cycle the prototype palette deterministically.
const TINT_KEYS: (keyof typeof TINTS)[] = ['blue', 'violet', 'green', 'amber', 'rose', 'cyan'];
const tintForIndex = (i: number): keyof typeof TINTS => TINT_KEYS[i % TINT_KEYS.length];

interface AudienceOption {
  id: string;
  name: string;
  count: number;
  color: keyof typeof TINTS;
}

// ════════ STEP CARD (collapsed in the timeline) ════════
interface StepCardProps {
  step: UiStep;
  label: string;
  index: number;
  total: number;
  editing: boolean;
  locked: boolean;
  canRemove: boolean;
  onToggleEdit: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}
const StepCard: React.FC<StepCardProps> = ({
  step, label, index, total, editing, locked, canRemove,
  onToggleEdit, onRemove, onMoveUp, onMoveDown,
}) => {
  const { t } = useTranslation(namespace);
  const tpl = step.template;
  const preview = templateToPreview(tpl);
  const isFollowup = index > 0;
  return (
    <div
      data-editing-card={editing ? 'true' : undefined}
      style={{
        border: editing ? '1px solid var(--bt-blue)' : '1px solid var(--line)',
        boxShadow: editing ? '0 0 0 1px var(--bt-blue) inset, 0 4px 16px rgba(29,142,255,.10)' : '0 1px 2px rgba(11,18,32,.04)',
        borderRadius: 12, background: 'var(--surface)', overflow: 'hidden', transition: 'box-shadow .15s, border-color .15s',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 12px' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6 }}>
          {isFollowup && <CornerDownRight size={13} stroke="var(--bt-blue-deep)" />}
          {label}
        </span>
        {locked && <span className="pill neutral" style={{ fontSize: 10 }}><Lock size={10} /> {t('campaignsRedesign.wizard.sentLocked', 'Already sent')}</span>}
        {preview.media && <AssetBadge kind={preview.media.kind} />}
        {preview.poll && <AssetBadge kind="poll" />}
        {preview.buttons && preview.buttons.length > 0 && <AssetBadge kind="buttons" />}
        <div style={{ flex: 1 }} />
        {!locked && (
          <>
            {total > 1 && (
              <div style={{ display: 'flex', gap: 1 }}>
                <button type="button" className="btn ghost icon sm" aria-label={t('campaignsRedesign.wizard.moveUp', 'Move up')} disabled={index === 0} onClick={onMoveUp} style={{ opacity: index === 0 ? 0.35 : 1 }}><ArrowUp size={13} /></button>
                <button type="button" className="btn ghost icon sm" aria-label={t('campaignsRedesign.wizard.moveDown', 'Move down')} disabled={index === total - 1} onClick={onMoveDown} style={{ opacity: index === total - 1 ? 0.35 : 1 }}><ArrowDown size={13} /></button>
              </div>
            )}
            <button type="button" className="btn sm" onClick={onToggleEdit}>
              {editing ? <><Check size={12} /> {t('common.done', 'Done')}</> : <><Pencil size={12} /> {t('common.edit', 'Edit')}</>}
            </button>
            {canRemove && <button type="button" className="btn danger-ghost icon sm" aria-label={t('common.remove', 'Remove')} onClick={onRemove}><Trash2 size={13} /></button>}
          </>
        )}
      </div>

      {editing && !locked ? (
        <div className="slide-down" style={{ padding: '4px 14px 16px', borderTop: '1px solid var(--line)' }}>
          <StepEditor idx={index} />
        </div>
      ) : (
        <div style={{ padding: '0 12px 12px' }}>
          <CampaignMessageBubble message={templateToPreviewMessage(tpl)} align="center" />
        </div>
      )}
    </div>
  );
};

// ════════ STEP 2 — MESSAGE (THE HEART) ════════
// `editingIdx` (which follow-up is open) is owned by the URL (`?step=<n>`);
// the parent passes it down + a setter that writes the query param.
interface MessageStepProps {
  steps: UiStep[];
  stopOnReply: boolean;
  setStopOnReply: (v: boolean) => void;
  editingIdx: number | null;
  setEditingIdx: (idx: number | null) => void;
}
const MessageStep: React.FC<MessageStepProps> = ({ steps, stopOnReply, setStopOnReply, editingIdx, setEditingIdx }) => {
  const { t } = useTranslation(namespace);
  const stepLabel = useStepLabel();
  const dispatch = useAppDispatch();
  const multi = steps.length > 1;

  const updateGap = (i: number, gap: DelayGap) =>
    dispatch(updateStepGapAction({ idx: i, gap }));

  // single→multi: give the previous last step a gap, push a new gapless step.
  const addFollowup = () => {
    // Pre-dispatch length == index of the newly-pushed last step.
    const newIdx = steps.length;
    dispatch(addFollowupAction());
    setEditingIdx(newIdx);
  };

  const removeStep = (i: number) => {
    dispatch(removeStepAction({ idx: i }));
    // If we removed the open editor (or one before it), clamp the open index.
    if (editingIdx != null) {
      const newLen = steps.length - 1;
      setEditingIdx(newLen <= 0 ? null : Math.min(editingIdx, newLen - 1));
    }
  };

  const move = (i: number, dir: number) => {
    dispatch(moveStepAction({ idx: i, dir }));
  };

  // Click-outside → collapse the open follow-up editor. Uses composedPath() (not
  // e.target) so it also works inside WhatsApp Web's shadow DOM — this wizard
  // ships in the extension content script. A click is IGNORED when it lands
  // inside the open card, or inside any popup the editor portals OUTSIDE the card
  // (the emoji MUI Menu, the @-mention typeahead listbox, the Templates dialog,
  // any MUI menu/popover), so picking an emoji / template / mention never
  // collapses the editor mid-edit. Anything else closes it.
  React.useEffect(() => {
    if (editingIdx == null) return;
    const POPUP_SELECTOR =
      '.MuiModal-root, .MuiPopover-root, .MuiPopper-root, .MuiMenu-root, ' +
      '[role="listbox"], [role="menu"], [role="dialog"], .epr-main, em-emoji-picker';
    const onPointerDown = (e: Event) => {
      for (const node of e.composedPath()) {
        if (!(node instanceof HTMLElement)) continue;
        if (node.dataset?.editingCard === 'true') return; // inside the open card
        if (node.matches?.(POPUP_SELECTOR)) return;        // inside an editor popup
      }
      setEditingIdx(null);
    };
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
  }, [editingIdx, setEditingIdx]);

  const heading = (
    <StepHeading
      step="message"
      title={multi
        ? t('campaignsRedesign.wizard.messageTitleMulti', 'Your message & follow-ups')
        : t('campaignsRedesign.wizard.messageTitle', 'Write your message')}
      subtitle={multi
        ? (stopOnReply
          ? t('campaignsRedesign.wizard.messageSubtitleMulti', 'Each follow-up goes out after the one before it — unless the person replies first.')
          : t('campaignsRedesign.wizard.messageSubtitleMultiNoStop', 'Each follow-up goes out after the one before it.'))
        : t('campaignsRedesign.wizard.messageSubtitle', 'Send one message, or add follow-ups for people who don’t reply.')}
    />
  );

  const addFollowupButton = (
    <button
      type="button"
      onClick={addFollowup}
      style={{
        appearance: 'none', cursor: 'pointer', width: '100%',
        border: '1.5px dashed var(--bt-blue-line)', background: 'var(--bt-blue-soft)',
        borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10,
        color: 'var(--bt-blue-deep)', transition: 'background .12s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bt-blue-line)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bt-blue-soft)'; }}
    >
      <span style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid var(--bt-blue-line)' }}>
        <Plus size={15} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{t('campaignsRedesign.wizard.addFollowup', 'Add a follow-up')}</span>
      <span style={{ fontSize: 11.5, color: 'var(--bt-blue-deep)', opacity: 0.8 }}>{t('campaignsRedesign.wizard.addFollowupHint', '— a reminder for people who haven’t replied')}</span>
    </button>
  );

  // The message step is a little wider than the other (single-column) wizard
  // steps because it carries a side-by-side live preview — but kept narrow
  // enough to keep the Audience step's generous side breathing room.
  const MESSAGE_STEP_MAX_WIDTH = 940;

  // ── PRISTINE SINGLE MESSAGE — looks like today's editor, open and ready ──
  if (!multi) {
    return (
      <div className="fade-in" style={{ maxWidth: MESSAGE_STEP_MAX_WIDTH, margin: '0 auto' }}>
        {heading}
        <StepEditor idx={0} />
        <div style={{ marginTop: 16 }}>{addFollowupButton}</div>
      </div>
    );
  }

  // ── MULTI — connected timeline of step cards (one expanded at a time) ──
  return (
    <div className="fade-in" style={{ maxWidth: MESSAGE_STEP_MAX_WIDTH, margin: '0 auto' }}>
      {heading}

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {steps.map((s, i) => (
          <React.Fragment key={s.template._id}>
            <div style={{ display: 'flex', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 26 }}>
                <div className="rail-node" style={{
                  background: i === 0 ? 'var(--bt-blue)' : 'var(--surface)',
                  color: i === 0 ? 'var(--on-brand)' : 'var(--bt-blue-deep)',
                  borderColor: 'var(--bt-blue-line)',
                }}>
                  {i + 1}
                </div>
                {i < steps.length - 1 && <div className="rail-line" />}
              </div>
              <div style={{ flex: 1, paddingBottom: i < steps.length - 1 ? 0 : 4 }}>
                <StepCard
                  step={s}
                  label={stepLabel(i)}
                  index={i}
                  total={steps.length}
                  editing={editingIdx === i}
                  locked={false}
                  canRemove={steps.length > 1}
                  onToggleEdit={() => setEditingIdx(editingIdx === i ? null : i)}
                  onRemove={() => removeStep(i)}
                  onMoveUp={() => move(i, -1)}
                  onMoveDown={() => move(i, +1)}
                />
                {i < steps.length - 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBlock: 12, marginInlineStart: -40, paddingInlineStart: 40 }}>
                    <DelayControl gap={s.gapAfter} onChange={(g) => updateGap(i, g)} />
                  </div>
                )}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      <div style={{ marginInlineStart: 40, marginTop: 4 }}>{addFollowupButton}</div>

      {/* STOP ON REPLY — only when multi */}
      <div className="slide-down" style={{
        marginTop: 18, padding: '14px 16px', borderRadius: 12,
        background: stopOnReply ? 'var(--wa-soft)' : 'var(--surface-2)',
        border: `1px solid ${stopOnReply ? 'var(--wa-line)' : 'var(--line)'}`,
        display: 'flex', alignItems: 'flex-start', gap: 12,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: stopOnReply ? 'var(--wa-deep)' : 'var(--surface-3)', color: stopOnReply ? 'var(--on-brand)' : 'var(--muted)',
        }}>
          <Reply size={16} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{t('campaignsRedesign.wizard.stopOnReplyTitle', 'Stop following up with anyone who replies')}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, lineHeight: 1.45 }}>
            {stopOnReply
              ? t('campaignsRedesign.wizard.stopOnReplyOn', 'As soon as someone replies, they stop getting the rest of the follow-ups.')
              : t('campaignsRedesign.wizard.stopOnReplyOff', 'Everyone gets every follow-up, even after they reply. Good for a regular reminder like a weekly check-in.')}
          </div>
        </div>
        <Toggle value={stopOnReply} onChange={setStopOnReply} />
      </div>
    </div>
  );
};

// ════════ PACING — the sentence that replaces the hidden toggles ════════
// The wizard used to carry an "Automatic pacing" toggle and a send-window
// toggle behind `SHOW_GLOBAL_LIMITERS = false`. Hiding them was right — they
// only ever set local state, and the window they promised is unimplemented — but
// removing them left the product silent about pacing that is REAL and
// UNCONDITIONAL: the backend gives every contact a due date 7 seconds after the
// previous one, so 500 contacts take about an hour and 2,000 take about four.
// The Review step meanwhile said "Sends as soon as you press send."
//
// This is the honest replacement: a statement of fact, not a control. Nothing
// here is settable, because nothing about it is settable.
//
// The three duration keys are held as module constants ending in `Key` so the
// translation-key guard can read them statically — `t(cond ? 'a' : 'b')` is
// exactly the shape it cannot follow.
const pacingDurationSecondKey = 'campaignsRedesign.pacing.durationSeconds';
const pacingDurationMinuteKey = 'campaignsRedesign.pacing.durationMinutes';
const pacingDurationHourKey = 'campaignsRedesign.pacing.durationHours';
// Named to END in `Key` (not `_KEY`) on purpose: the translation-key guard skips
// a `t(x)` call whose argument matches /Key/, and SCREAMING_CASE would put a
// `Y` where it wants a `y`, so the call would be reported as unreadable.
const pacingDurationKey: Record<PacingUnit, string> = {
  second: pacingDurationSecondKey,
  minute: pacingDurationMinuteKey,
  hour: pacingDurationHourKey,
};

const pacingRangeKey: Record<PacingUnit, string> = {
  second: 'campaignsRedesign.pacing.rangeSeconds',
  minute: 'campaignsRedesign.pacing.rangeMinutes',
  hour: 'campaignsRedesign.pacing.rangeHours',
};

interface PacingNoteProps {
  /** Reach across every chosen audience — the same upper bound the Review shows. */
  totalContacts: number;
  /** `full` on the Timing step (its own row); `inline` inside the Review card. */
  variant?: 'full' | 'inline';
  /** #412: opt-in random gap. Absent = the fixed spacing sentence, unchanged. */
  randomGap?: CampaignRandomGap;
}
const PacingNote: React.FC<PacingNoteProps> = ({ totalContacts, variant = 'full', randomGap }) => {
  const { t } = useTranslation(namespace);
  let text: string;
  if (randomGap) {
    // Worth saying once the LONGEST plausible span reaches a minute (the range is what we state).
    const range = describeCampaignSendRange(totalContacts, randomGap);
    if (range.maxSeconds < 60) return null;
    const duration = range.minValue === range.maxValue
      ? t(pacingDurationKey[range.unit], { count: range.maxValue })
      : t(pacingRangeKey[range.unit], { from: range.minValue, to: range.maxValue });
    text = t(
      'campaignsRedesign.pacing.noteRandom',
      'Contacts are messaged one at a time, with a random {{min}}–{{max}} second gap between each, so {{contacts}} takes around {{duration}} to go out in full.',
      {
        min: randomGap.minSeconds,
        max: randomGap.maxSeconds,
        contacts: t('campaignsRedesign.counts.contacts', { count: totalContacts, n: totalContacts.toLocaleString() }),
        duration,
      },
    );
  } else {
    // Below a minute the pacing is invisible to a human; saying so every time
    // would train people to skip the line, and this line exists for the campaign
    // that takes an afternoon.
    if (!pacingIsWorthStating(totalContacts)) return null;
    const span = describeCampaignSendSpan(totalContacts);
    const duration = t(pacingDurationKey[span.unit], { count: span.value });
    text = t(
      'campaignsRedesign.pacing.note',
      'Contacts are messaged one at a time, about {{gap}} seconds apart, so {{contacts}} takes around {{duration}} to go out in full.',
      {
        gap: CAMPAIGN_SEND_SPACING_SECONDS,
        contacts: t('campaignsRedesign.counts.contacts', { count: totalContacts, n: totalContacts.toLocaleString() }),
        duration,
      },
    );
  }
  if (variant === 'inline') {
    return (
      <div style={{ marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
        <Gauge size={12} style={{ marginTop: 2, flexShrink: 0 }} />
        <span>{text}</span>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 14, display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 13px', background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 10 }}>
      <Gauge size={14} stroke="var(--muted)" style={{ marginTop: 1, flexShrink: 0 }} />
      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>{text}</div>
    </div>
  );
};

// ── #412: OPTIONAL randomized gap between messages ──
// Off by default (the fixed spacing above stays the truth). When on, the person picks a min and a
// max in whole seconds; each gap is drawn at random in between by the backend. Invalid entries are
// NEVER committed to the timing (the autosave would be rejected, and a silently-ignored value is
// worse than a visible error): the last valid pair stays in force, the field shows why, and a
// blur reverts the text to that pair.
interface RandomGapControlProps {
  value?: CampaignRandomGap;
  onChange: (next: CampaignRandomGap | undefined) => void;
}
const RandomGapControl: React.FC<RandomGapControlProps> = ({ value, onChange }) => {
  const { t } = useTranslation(namespace);
  const [minText, setMinText] = React.useState(String(value?.minSeconds ?? DEFAULT_RANDOM_GAP.minSeconds));
  const [maxText, setMaxText] = React.useState(String(value?.maxSeconds ?? DEFAULT_RANDOM_GAP.maxSeconds));
  const enabled = Boolean(value);

  const parse = (text: string) => (text.trim() === '' ? NaN : Number(text));
  const error = randomGapFieldError(parse(minText), parse(maxText));

  const edit = (nextMin: string, nextMax: string) => {
    setMinText(nextMin);
    setMaxText(nextMax);
    const a = parse(nextMin);
    const b = parse(nextMax);
    if (randomGapFieldError(a, b) === null) onChange({ minSeconds: a, maxSeconds: b });
  };
  const revert = () => {
    if (!value) return;
    setMinText(String(value.minSeconds));
    setMaxText(String(value.maxSeconds));
  };
  const errorText = error === 'order'
    ? t('campaignsRedesign.randomGap.errorOrder', 'The shortest gap can’t be longer than the longest.')
    : error
      ? t('campaignsRedesign.randomGap.errorRange', 'Use whole seconds from {{min}} to {{max}}.', { min: CAMPAIGN_RANDOM_GAP_MIN_SECONDS, max: CAMPAIGN_RANDOM_GAP_MAX_SECONDS })
      : null;

  const field = (id: string, label: string, text: string, onText: (v: string) => void, invalid: boolean) => (
    <label htmlFor={id} style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, color: 'var(--muted)' }}>
      {label}
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={CAMPAIGN_RANDOM_GAP_MIN_SECONDS}
        max={CAMPAIGN_RANDOM_GAP_MAX_SECONDS}
        step={1}
        value={text}
        aria-invalid={invalid}
        onChange={(e) => onText(e.target.value)}
        onBlur={revert}
        style={{
          width: 96, padding: '7px 9px', borderRadius: 8, fontSize: 13, color: 'var(--ink)', background: 'var(--surface)',
          border: `1px solid ${invalid ? 'var(--danger, #d33)' : 'var(--line-strong)'}`,
        }}
      />
    </label>
  );

  return (
    <div style={{ marginTop: 14, padding: '12px 14px', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>{t('campaignsRedesign.randomGap.title', 'Randomize the gap between messages')}</div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2, lineHeight: 1.45 }}>
            {t('campaignsRedesign.randomGap.hint', 'Off by default. When on, each wait is picked at random between the two limits you set, instead of a steady beat.')}
          </div>
        </div>
        <Toggle
          value={enabled}
          onChange={(on) => {
            if (!on) { onChange(undefined); return; }
            setMinText(String(DEFAULT_RANDOM_GAP.minSeconds));
            setMaxText(String(DEFAULT_RANDOM_GAP.maxSeconds));
            onChange({ minSeconds: DEFAULT_RANDOM_GAP.minSeconds, maxSeconds: DEFAULT_RANDOM_GAP.maxSeconds });
          }}
        />
      </div>
      {enabled && (
        <div className="fade-in" style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
            {field('random-gap-min', t('campaignsRedesign.randomGap.min', 'Shortest wait (seconds)'), minText, (v) => edit(v, maxText), error === 'min' || error === 'order')}
            {field('random-gap-max', t('campaignsRedesign.randomGap.max', 'Longest wait (seconds)'), maxText, (v) => edit(minText, v), error === 'max' || error === 'order')}
          </div>
          {errorText && (
            <div role="alert" style={{ marginTop: 8, fontSize: 11.5, color: 'var(--danger, #d33)' }}>{errorText}</div>
          )}
        </div>
      )}
    </div>
  );
};

// ════════ STEP 3 — TIMING ════════
// TimingState moved to @common-reducers/DripEditorReducer (imported above).
interface TimingStepProps {
  timing: TimingState;
  setTiming: (updater: (prev: TimingState) => TimingState) => void;
  multi: boolean;
  steps: UiStep[];
  /** Reach across every chosen audience — drives the pacing sentence. */
  totalContacts: number;
}
const TimingStep: React.FC<TimingStepProps> = ({ timing, setTiming, multi, steps, totalContacts }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const set = (patch: Partial<TimingState>) => setTiming((prev) => ({ ...prev, ...patch }));

  // ── Real schedule picker ↔ drip timing bridge ──
  // The date/time picker (SchedulePickerStandaloneWrapper) is the SAME control the
  // scheduled-message dialog uses; it reads/writes ScheduledMessageEditorReducer
  // .dueDate and self-defaults to "now". We bridge that reducer to the drip's
  // persisted timing: on entering schedule mode restore a previously-chosen time
  // (else the picker's own default seeds "now"), and mirror every picker change
  // back into `timing.scheduledAt` so the wizard autosave persists schedule.dueDate.
  const pickerDueDate = useAppSelector((s) => s.ScheduledMessageEditorReducer.dueDate);
  // FE#240: the picker's recurrence UI writes these; bridge them like dueDate.
  const pickerIsRecurring = useAppSelector((s) => s.ScheduledMessageEditorReducer.isRecurring);
  const pickerRruleset = useAppSelector((s) => s.ScheduledMessageEditorReducer.rruleset);
  React.useEffect(() => {
    if (timing.launch === 'schedule') {
      if (timing.scheduledAt) dispatch(setDueDate(timing.scheduledAt));
      // Restore a previously-chosen recurrence into the picker on re-entry.
      dispatch(setIsRecurring(Boolean(timing.isRecurring && timing.rruleset)));
      dispatch(setRruleset(timing.rruleset ?? ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timing.launch]);
  React.useEffect(() => {
    if (timing.launch !== 'schedule') return;
    const nextRecurring = Boolean(pickerIsRecurring && pickerRruleset);
    const nextRruleset = nextRecurring ? pickerRruleset : undefined;
    if (
      (pickerDueDate && pickerDueDate !== timing.scheduledAt) ||
      nextRecurring !== Boolean(timing.isRecurring) ||
      nextRruleset !== timing.rruleset
    ) {
      setTiming((prev) => ({
        ...prev,
        scheduledAt: pickerDueDate || prev.scheduledAt,
        isRecurring: nextRecurring,
        rruleset: nextRruleset,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerDueDate, pickerIsRecurring, pickerRruleset, timing.launch]);

  return (
    <div className="fade-in" style={{ maxWidth: WIZARD_STEP_MAX, margin: '0 auto' }}>
      <StepHeading
        step="timing"
        title={multi
          ? t('campaignsRedesign.wizard.timingTitleMulti', 'When does the first message go out?')
          : t('campaignsRedesign.wizard.timingTitle', 'When should it send?')}
        subtitle={multi
          ? t('campaignsRedesign.wizard.timingSubtitleMulti', 'The follow-ups go out after this, using the delays you set on each message.')
          : t('campaignsRedesign.wizard.timingSubtitle', 'Send right away, or schedule it for later.')}
      />

      <div style={{ marginBottom: 18 }}>
        <Eyebrow style={{ marginBottom: 8 }}>{t('campaignsRedesign.wizard.firstMessage', 'First message')}</Eyebrow>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="choice" data-active={timing.launch === 'now'} onClick={() => set({ launch: 'now' })} style={{ alignItems: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: timing.launch === 'now' ? 'var(--bt-blue)' : 'var(--surface-3)', color: timing.launch === 'now' ? 'var(--on-brand)' : 'var(--muted)' }}>
              <Zap size={16} />
            </div>
            <div style={{ flex: 1, textAlign: 'start' }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{t('campaignsRedesign.wizard.startNow', 'Send now')}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('campaignsRedesign.wizard.startNowHint', 'Sending starts the moment you press the button')}</div>
            </div>
          </button>
          <button className="choice" data-active={timing.launch === 'schedule'} onClick={() => set({ launch: 'schedule' })} style={{ alignItems: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: timing.launch === 'schedule' ? 'var(--bt-blue)' : 'var(--surface-3)', color: timing.launch === 'schedule' ? 'var(--on-brand)' : 'var(--muted)' }}>
              <Calendar size={16} />
            </div>
            <div style={{ flex: 1, textAlign: 'start' }}>
              <div style={{ fontSize: 13.5, fontWeight: 600 }}>{t('campaignsRedesign.wizard.scheduleLater', 'Schedule for later')}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('campaignsRedesign.wizard.scheduleLaterHint', 'Pick the date and time to start')}</div>
            </div>
          </button>
        </div>

        {/* Real date + time picker — the same control the scheduled-message dialog
            uses (date calendar + inline-editable time), seeded to "now". Rendered
            below the choices (not inside the button) so interacting with it never
            toggles the choice. */}
        {timing.launch === 'schedule' && (
          <div
            className="fade-in campaigns-rd-schedule-picker"
            style={{ marginTop: 10, padding: '12px 14px', border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface)' }}
          >
            <SchedulePickerStandaloneWrapper
              showRecurrence
              showDeliveryOptions={false}
              allowedFrequencies={CAMPAIGN_RECURRENCE_FREQUENCIES}
            />
            {/* BE#657 — the pickers no longer self-seed "now", so a scheduled launch
                with no time picked is possible; Continue/Launch are gated on it
                (scheduledLaunchNeedsTime). State WHY here, on the step the user must
                fix, so the disabled button and the post-launch redirect are explained.
                Same key the requestLaunch redirect sets. */}
            {scheduledLaunchNeedsTime(timing) && (
              <div role="alert" style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: 'var(--danger, #d33)' }}>
                {t('campaignsRedesign.wizard.pickTimeBeforeLaunch', 'Pick a send time before scheduling the campaign.')}
              </div>
            )}
            {/* The recurrence control inside the picker is labelled "Edit
                recurrence" — it names the FIELD it writes and never says what the
                recurrence currently is, so the one fact the person needs at this
                moment ("so when does this actually go out again?") was reachable
                only by opening the popover they had just closed. The schedule is
                stated here instead, in the same words the Review step and the
                planner use for the same thing. */}
            {timing.isRecurring && timing.rruleset && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: 'var(--bt-blue-deep)' }}>
                <Repeat size={13} />
                <span>{t('campaignsRedesign.wizard.repeats', 'Repeats {{summary}}', { summary: recurrenceSummary(timing.rruleset, t) })}</span>
              </div>
            )}
          </div>
        )}
      </div>


      <RandomGapControl value={timing.randomGap} onChange={(randomGap) => set({ randomGap })} />
      <PacingNote totalContacts={totalContacts} randomGap={timing.randomGap} />

      {multi && (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 13px', background: 'var(--surface-2)', border: '1px dashed var(--line-strong)', borderRadius: 10 }}>
          <Layers size={14} stroke="var(--muted)" style={{ marginTop: 1 }} />
          <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--ink-2)' }}>{t('campaignsRedesign.wizard.gapsLiveWithMessagesTitle', 'The waits between messages are set on the messages.')}</strong>{' '}
            {t(
              'campaignsRedesign.wizard.gapsLiveWithMessagesBody',
              'You chose “{{gaps}}”. To change that, go back to the Message step — this screen only sets when the first message goes out.',
              { gaps: steps.filter((s) => s.gapAfter).map((s) => gapText(t, s.gapAfter)).join(', ') },
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ════════ STEP 4 — FINISH / REVIEW ════════
interface SummaryCardProps {
  icon: LucideIcon;
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
  warn?: boolean;
}
const SummaryCard: React.FC<SummaryCardProps> = ({ icon, title, onEdit, children, warn }) => {
  const { t } = useTranslation(namespace);
  const I = icon || Dot;
  return (
    <div style={{ border: `1px solid ${warn ? 'var(--kbmiss-line)' : 'var(--line)'}`, borderRadius: 12, background: warn ? 'var(--kbmiss-soft)' : 'var(--surface)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px', borderBottom: '1px solid var(--line)' }}>
        <I size={15} stroke="var(--muted)" />
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{title}</span>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn ghost sm" onClick={onEdit}><Pencil size={11} /> {t('common.edit', 'Edit')}</button>
      </div>
      <div style={{ padding: '12px 14px' }}>{children}</div>
    </div>
  );
};

interface FinishStepProps {
  steps: UiStep[];
  stopOnReply: boolean;
  timing: TimingState;
  /** Every chosen audience, in the order the user ticked them. */
  audiences: AudienceOption[];
  multi: boolean;
  onJump: (id: WizardStepId) => void;
}
const FinishStep: React.FC<FinishStepProps> = ({ steps, stopOnReply, timing, audiences, multi, onJump }) => {
  const { t } = useTranslation(namespace);
  // The "you picked a time that has already passed" banner, the warn styling on
  // the timing card and the "Pick a new time" button below were all wired to a
  // literal `false` at the call site, so none of them had ever appeared. It is
  // computed here now, off the SAME pure predicate the compose dialog's Send gate
  // uses, on a ticking clock — a review step is exactly the screen someone leaves
  // open while they think, so the answer changes with no input from them.
  //
  // Left as a WARNING here rather than a block: a campaign with a passed start
  // still launches, and turning that into a refusal is a send-path decision that
  // needs eyes on the launch flow. Saying nothing was not defensible; refusing
  // silently would be worse.
  const now = useNowTicker();
  const scheduledPassed = timing.launch === 'schedule' && resolvePastTimeState({
    dueDate: timing.scheduledAt,
    isRecurring: timing.isRecurring,
    rruleset: timing.rruleset,
    now,
  }).warn;
  const stepLabel = useStepLabel();
  // Sum across the chosen boards. This is the reach BEFORE the backend dedupes by
  // whatsappId, so a contact sitting in two audiences is counted twice here and
  // messaged once at send — an upper bound, never an undercount.
  const totalContacts = audiences.reduce((n, a) => n + a.count, 0);
  const countLabel = audiences.length > 0 ? totalContacts.toLocaleString() : '';
  // When scheduled, surface the exact start date + time (not just "Scheduled").
  // Mirrors the launched-screen formatting for consistency.
  const scheduledStart = timing.launch === 'schedule' && timing.scheduledAt ? new Date(timing.scheduledAt) : null;
  const scheduledStartLabel =
    scheduledStart && !isNaN(scheduledStart.getTime())
      ? scheduledStart.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
      : null;
  return (
    <div className="fade-in" style={{ maxWidth: WIZARD_STEP_MAX, margin: '0 auto' }}>
      <StepHeading
        step="review"
        title={t('campaignsRedesign.wizard.reviewTitle', 'You’re all set')}
        /* `countLabel` is '' when no audience has been picked, and the two
           sentences below interpolate it — so the Review used to greet an
           audience-less campaign with the literal string "One message to
           contacts.", a sentence with a hole where the number should be. A
           missing audience is not a formatting edge case, it is the one thing
           standing between this person and a campaign that reaches nobody, so
           it gets said. */
        /* Two counts in one sentence, and i18next can pluralise on only one — so
           each half is its own pluralised phrase and the locale owns the frame
           that joins them. Written as one template it produced "4 messages to 1
           contacts": the "s" was a literal, which is only ever right by luck, and
           is wrong in every language with a dual form. */
        subtitle={audiences.length === 0
          ? t('campaignsRedesign.wizard.reviewSubtitleNoAudience', 'Pick who this goes to before you send it.')
          : t('campaignsRedesign.wizard.reviewSubtitleComposed', '{{messages}} to {{contacts}}.', {
            messages: t('campaignsRedesign.counts.messages', { count: steps.length }),
            contacts: t('campaignsRedesign.counts.contacts', { count: totalContacts, n: countLabel }),
          })}
      />

      {scheduledPassed && (
        <div style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 13px', background: 'var(--kbmiss-soft)', border: '1px solid var(--kbmiss-line)', borderRadius: 10 }}>
          <AlertTriangle size={15} stroke="var(--kbmiss)" />
          <span style={{ fontSize: 12.5, color: 'var(--kbmiss)' }}>{t('campaignsRedesign.wizard.scheduledPassed', 'The time you picked has already passed. Pick a new one, or send now.')}</span>
          <button type="button" className="btn sm" style={{ marginInlineStart: 'auto' }} onClick={() => onJump('timing')}>{t('campaignsRedesign.wizard.fixTime', 'Pick a new time')}</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Audience + Timing FIRST — the who/when a user checks at a glance before
            scanning the message content below. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <SummaryCard
            icon={Users}
            /* Was a `> 1` ternary between two hand-written strings — English
               grammar decided in code, so no locale could apply its own rule
               (Hebrew inflects two differently from three). */
            title={t('campaignsRedesign.wizard.audienceCardTitle', { count: audiences.length })}
            onEdit={() => onJump('audience')}
          >
            {audiences.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <AudAvatar color="blue" icon={Users} size={32} />
                {/* Was an em-dash stacked on the words "— contacts": two
                    dashes and a plural noun, which state neither that an
                    audience is missing nor what to do about it. */}
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {t('campaignsRedesign.wizard.noAudienceYet', 'No audience chosen')}
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                    {t('campaignsRedesign.wizard.noAudienceYetHint', 'Nobody will receive this yet')}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* One row per chosen audience — the whole point of #161 is that the
                    user can SEE everything the campaign will reach before launching. */}
                {audiences.map((a) => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <AudAvatar color={a.color ?? 'blue'} icon={Users} size={32} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{a.name}</div>
                      <div className="num" style={{ fontSize: 11.5, color: 'var(--muted)' }}>
                        {/* One pluralised key, not a ternary over two: `count`
                            picks the form, `n` carries the locale-formatted
                            number ("1,240", "1.240"). */}
                        {t('campaignsRedesign.counts.contacts', { count: a.count, n: a.count.toLocaleString() })}
                      </div>
                    </div>
                  </div>
                ))}
                {audiences.length > 1 && (
                  <div className="num" style={{ fontSize: 11.5, color: 'var(--muted)', paddingTop: 2, borderTop: '1px solid var(--line)' }}>
                    {t('campaignsRedesign.wizard.contactsTotal', '{{n}} contacts in total', { count: totalContacts, n: countLabel })}
                  </div>
                )}
              </div>
            )}
          </SummaryCard>

          <SummaryCard icon={Clock} title={t('campaignsRedesign.wizard.timingCardTitle', 'When it sends')} onEdit={() => onJump('timing')} warn={scheduledPassed}>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.6 }}>
              {/* The two branches were inconsistent: the `now` branch carries its
                  verb ("Sends as soon as…") while the scheduled branch was the
                  bare adjective "Scheduled" stacked over a bare timestamp — a
                  value with its question deleted, the same defect the detail
                  screen fixed by naming its sending window. The date now
                  arrives attached to the sentence it answers. */}
              {/* Was "Sends as soon as you press send" — a claim the send path
                  does not honour. The first message does leave immediately; the
                  campaign does not, because every contact after the first is
                  spaced 7 seconds behind the one before (campaign-pacing.ts).
                  The verb now covers only what is true, and PacingNote below
                  states the rest with the real audience size in it. */}
              {timing.launch === 'now' ? (
                <div><strong>{t('campaignsRedesign.wizard.startsImmediately', 'Starts sending the moment you press send')}</strong></div>
              ) : (
                <>
                  <div><strong>{t('campaignsRedesign.wizard.startsSendingLabel', 'Starts sending')}</strong></div>
                  <div className="num" style={{ marginTop: 2, fontSize: 11.5, color: 'var(--muted)' }}>
                    {scheduledStartLabel ?? t('campaignsRedesign.wizard.startTimeNotSet', 'No time picked yet')}
                  </div>
                </>
              )}
              {/* FE#240: recurring campaigns repeat on a cadence — surface it so the
                  Review reflects what the ORCH#102 engine will re-fire. */}
              {timing.launch === 'schedule' && timing.isRecurring && timing.rruleset && (
                <div style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, color: 'var(--bt-blue-deep)' }}>
                  <Repeat size={12} /> {t('campaignsRedesign.wizard.repeats', 'Repeats {{summary}}', { summary: recurrenceSummary(timing.rruleset, t) })}
                </div>
              )}
              <PacingNote totalContacts={totalContacts} variant="inline" randomGap={timing.randomGap} />
            </div>
          </SummaryCard>
        </div>

        {/* Message flow — a minimized, read-only COLUMN of the SAME WhatsApp
            preview bubble the composer/list use (no edit/move/delete controls),
            with the wait between steps shown inline. Reads like the sent thread. */}
        <SummaryCard icon={Layers} title={multi
          ? t('campaignsRedesign.wizard.messageFlowTitle', 'Messages')
          : t('campaignsRedesign.wizard.messageCardTitle', 'Message')} onEdit={() => onJump('message')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {steps.map((s, i) => {
              const pm = templateToPreviewMessage(s.template);
              const isLast = i === steps.length - 1;
              return (
                <React.Fragment key={s.template._id}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span className="rail-node" style={{ width: 20, height: 20, fontSize: 10, background: i === 0 ? 'var(--bt-blue)' : 'var(--surface)', color: i === 0 ? 'var(--on-brand)' : 'var(--bt-blue-deep)' }}>{i + 1}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink-2)' }}>{stepLabel(i)}</span>
                    </div>
                    {isEmptyPreview(pm)
                      ? <span style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', paddingInlineStart: 27 }}>{t('campaignsRedesign.wizard.noMessageSet', 'Nothing written yet')}</span>
                      : <CampaignMessageBubble message={pm} align="end" />}
                  </div>
                  {!isLast && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingInlineStart: 5, color: 'var(--muted)' }}>
                      <Clock size={11} />
                      <span style={{ fontSize: 10.5 }}>
                        <Trans
                          t={t}
                          i18nKey="campaignsRedesign.wizard.waitsBeforeNext"
                          defaults="waits <num>{{gap}}</num> before the next"
                          values={{ gap: gapText(t, s.gapAfter) }}
                          // Not the mono `.num` face: the gap is "2 days", and a monospace
                          // space next to proportional text read as a double space.
                          components={{ num: <span style={{ fontWeight: 600 }} /> }}
                        />
                      </span>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </SummaryCard>

        {multi && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 14px', borderRadius: 12, background: stopOnReply ? 'var(--wa-soft)' : 'var(--surface-2)', border: `1px solid ${stopOnReply ? 'var(--wa-line)' : 'var(--line)'}` }}>
            <Reply size={15} stroke={stopOnReply ? 'var(--wa-deep)' : 'var(--muted)'} />
            <span style={{ fontSize: 12.5, color: 'var(--ink-2)', flex: 1 }}>
              {stopOnReply
                ? <Trans t={t} i18nKey="campaignsRedesign.wizard.reviewStopOnReplyOn" defaults="Follow-ups <strong>stop</strong> for anyone who replies." components={{ strong: <strong /> }} />
                : <Trans t={t} i18nKey="campaignsRedesign.wizard.reviewStopOnReplyOff" defaults="Everyone gets <strong>all the follow-ups</strong>, even after they reply." components={{ strong: <strong /> }} />}
            </span>
            <button type="button" className="btn ghost sm" onClick={() => onJump('message')}>{t('campaignsRedesign.wizard.change', 'Change')}</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ════════ DRIP ↔ UiStep CONVERSION ════════
// Persist: UiStep template (editor-model) → bt-common campaign Template. The
// synthetic bridge `_id` is intentionally dropped (drip steps don't persist as
// standalone saved templates).
const toTemplate = (t: DripStepTemplate): Template => ({
  message: t.message ?? undefined,
  asset: t.asset ? { url: t.asset.url ?? '', type: t.asset.type ?? null, name: t.asset.name ?? null } : undefined,
  isPoll: t.isPoll,
  poll: t.poll,
  sendLinkPreview: t.sendLinkPreview,
  linkPreview: t.linkPreview,
  buttons: Array.isArray(t.buttons)
    ? t.buttons.map((b) => ({ buttonType: b.buttonType, value: b.value }))
    : undefined,
  deleted: false,
});

// Hydrate: a persisted bt-common campaign Template → the editor-model
// DripStepTemplate the wizard/editor work in. A FRESH bridge `_id` is minted so
// the singleton editor reseed is keyed correctly for this session.
const toUiTemplate = (t: Template | undefined): DripStepTemplate => {
  const editorShape = (t ?? {}) as Partial<EditorTemplate>;
  return { ...editorShape, _id: nextBridgeId() };
};

// Build the persisted step list from the UI working copy. Last step never has a
// gap (schema: gapAfter optional).
export const buildSteps = (steps: UiStep[]): DripStep[] =>
  steps.map((s, i) => ({
    stepIndex: i,
    template: toTemplate(s.template),
    ...(i < steps.length - 1 && s.gapAfter ? { gapAfter: s.gapAfter } : {}),
  }));

// The persisted DripStepGap.unit reuses the wider campaign RecurrenceUnit enum
// (incl. month/year), but the UI DelayControl only handles minute/hour/day/week.
// Narrow on hydrate — an out-of-range unit (month/year, never produced by this
// UI) falls back to the default gap rather than breaking the control's type.
const DELAY_UNITS: readonly DelayUnit[] = ['minute', 'hour', 'day', 'week'];
const toDelayGap = (gap: DripStep['gapAfter']): DelayGap => {
  if (gap && (DELAY_UNITS as readonly string[]).includes(gap.unit)) {
    return { value: gap.value, unit: gap.unit as DelayUnit };
  }
  return defaultGap();
};

// Cut a string to at most `max` characters at a WORD boundary.
//
// A blind `slice(0, 45)` cut mid-word ("…longer than f…"), which reads as
// damage rather than as abbreviation — the wizard header looked like it had a
// rendering bug the moment a message was typed. Falls back to the hard slice
// only when there is no space to break on inside the budget (a single very long
// word, or a script that does not space-separate), because a word-boundary rule
// that returns almost nothing is worse than a hard cut.
export const truncateAtWord = (text: string, max: number): string => {
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  // Only honour the break if it keeps a useful amount of the line.
  if (lastSpace >= Math.floor(max * 0.6)) return slice.slice(0, lastSpace).trimEnd();
  return slice.trimEnd();
};

/** Longest auto-derived name before it is abbreviated. */
export const NAME_MAX = 45;

// Auto-name a campaign from its content when the user didn't type a name — the
// first message's opening line (WhatsApp has no subject, so this reads as one),
// else the audience, else a generic label. Keeps names meaningful instead of a
// wall of "Untitled campaign".
// `t` is REQUIRED for the same reason it is on `deriveAudienceLabel`: the two
// fallbacks below ARE the campaign's name — they render as the name field's
// placeholder and are persisted verbatim on save — and they were being built in
// English grammar in code, so a Hebrew user's campaign was called "Campaign to …"
// in a language they do not read, with nothing in either locale file a translator
// could reach. An optional parameter would have left every existing call site
// quietly wrong; a required one makes the compiler find them.
export const deriveCampaignName = (steps: UiStep[], t: Translate, audienceName?: string): string => {
  const firstMsg = steps[0]?.template?.message?.trim();
  if (firstMsg) {
    const firstLine = firstMsg.split('\n')[0].trim();
    return firstLine.length > NAME_MAX ? `${truncateAtWord(firstLine, NAME_MAX)}…` : firstLine;
  }
  if (audienceName?.trim()) {
    return t('campaignsRedesign.wizard.autoNameToAudience', {
      defaultValue: 'Campaign to {{audience}}',
      audience: audienceName.trim(),
    });
  }
  return t('campaignsRedesign.wizard.autoNameNew', { defaultValue: 'New campaign' });
};

// Hydrate the UI working copy from a persisted draft. An empty/new draft starts
// with one pristine, gapless step.
export const hydrateSteps = (drip: Drip | undefined): UiStep[] => {
  const persisted = drip?.steps ?? [];
  if (persisted.length === 0) {
    return [{ stepIndex: 0, template: emptyTemplate(), gapAfter: null }];
  }
  return persisted.map((s, i) => ({
    stepIndex: i,
    template: toUiTemplate(s.template as Template | undefined),
    gapAfter: i < persisted.length - 1 ? toDelayGap(s.gapAfter) : null,
  }));
};

// hydrateTiming / buildSchedule live in the pure, node-tested module
// `campaign-timing-schedule.ts` (they own the recurrence payload contract for
// FE#240) and are imported at the top of this file. Re-exported here because
// drip-step-edit-modal.tsx imports hydrateTiming from the wizard.
export { hydrateTiming, buildSchedule };

// ════════ WIZARD SHELL (route: /campaigns/:id/edit/:step) ════════
export interface CampaignWizardProps {
  /** The draft Drip id from the route. */
  dripId: string;
  /** The active step, from the `/edit/:step` path segment. */
  step: WizardStepId;
}

const AUTOSAVE_DEBOUNCE_MS = 600;

// FE#332 — a banner over a wizard step while the drip is ACTIVELY sending.
//   • locked=false (INFO): the step stays fully interactive; the banner just
//     explains scope (e.g. an audience edit applies to upcoming messages only).
//   • locked=true: the step is frozen into Redis at run-start (the current
//     step's send time), so the banner is a Lock and the content is rendered
//     non-interactive. When `show` is false the step renders untouched.
const StepNotice: React.FC<{ show: boolean; locked?: boolean; note: string; children: React.ReactNode }> = ({ show, locked = false, note, children }) => {
  if (!show) return <>{children}</>;
  const Icon = locked ? Lock : Info;
  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div role="note" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', marginBottom: 14, borderRadius: 8, border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--muted)', fontSize: 12.5 }}>
        <Icon size={14} style={{ flexShrink: 0 }} /> {note}
      </div>
      {locked ? <div aria-disabled style={{ pointerEvents: 'none', opacity: 0.55 }}>{children}</div> : children}
    </div>
  );
};

export const CampaignWizard: React.FC<CampaignWizardProps> = ({ dripId, step }) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [launchDrip] = useLaunchDripMutation();

  // ── Load the persisted draft by id (fetch on mount if absent) ──
  const draft = useAppSelector((s) => dripsSelectOneObjectByQuery(s.DBDripReducer, { _id: dripId })) as Drip | undefined;
  React.useEffect(() => {
    dispatch(DBDripThunks.find({ _id: dripId }));
  }, [dispatch, dripId]);

  // Load the user's audience boards so the picker is populated on a fresh route.
  React.useEffect(() => {
    // Load ALL audiences — AUDIENCE_LIST_QUERY carries the high $limit (server default
    // paginate of 10 otherwise hides audiences past the first ten from the picker).
    dispatch(DBBoardsThunks.find({ type: 'audience', deleted: { $ne: true }, ...AUDIENCE_LIST_QUERY }));
  }, [dispatch]);

  // FE#332 — EDIT reuses this CREATE wizard, prepopulated, and saves back to the
  // SAME drip. What the finish button DOES is status-aware: a draft LAUNCHES; an
  // already-launched drip SAVES IN PLACE (patch, never launchDrip → double-send
  // safe). And when the drip is ACTIVELY dispatching its current step, that
  // step's audience + time are frozen into Redis, so the wizard shows those
  // controls read-only. Load the current step campaign so we can tell a
  // scheduled/pending step (fully editable) from a running one.
  React.useEffect(() => {
    dispatch(DBCampaignThunks.find({ dripId }));
  }, [dispatch, dripId]);
  const completionMode = resolveWizardCompletionMode(draft?.status, draft?.startedAt);
  const isEditingLaunched = completionMode === 'save-edits';
  const currentStepCampaign = useAppSelector((s) =>
    isEditingLaunched
      ? campaignsSelectOneObjectByQuery(s.DBCampaignReducer, { dripId, dripStepIndex: draft?.currentStep ?? 0 })
      : undefined,
  ) as { campaignStatus?: string } | undefined;
  // True only while the current step is actually SENDING. A pending/scheduled
  // step (the common "wrong group on a scheduled send" case) is fully editable —
  // the orchestrator propagates the change to it. While actively sending:
  //   • AUDIENCE stays EDITABLE — the edit lands on the drip and only future/
  //     pending steps pick it up (syncPendingStepCampaign is pending-only, so it
  //     never re-targets the sent/running step) → no Redis reconciliation. We
  //     just note that it applies to upcoming messages.
  //   • the current step's SEND TIME is frozen into Redis → read-only.
  const isActivelySending = isEditingLaunched && isDripActivelySending(currentStepCampaign?.campaignStatus);

  // ── LIVE WORKING COPY (owned by DripEditorReducer, hydrated ONCE from the
  //     draft, then autosaved back) ──
  // Per the state-flow doc, the in-progress drip content is the single source of
  // truth held in the slice; we hydrate it from the persisted draft the first
  // time the draft arrives, and autosave changes back (debounced). The URL +
  // persisted draft remain the source of truth for reload — the slice is the
  // working copy.
  const steps = useAppSelector((s) => s.DripEditorReducer.steps);
  const name = useAppSelector((s) => s.DripEditorReducer.name);
  const stopOnReply = useAppSelector((s) => s.DripEditorReducer.stopOnReply);
  const timing = useAppSelector((s) => s.DripEditorReducer.timing);
  const hydrated = useAppSelector((s) => s.DripEditorReducer.hydrated);
  const sliceDripId = useAppSelector((s) => s.DripEditorReducer.dripId);
  // The slice is a store singleton that survives unmount, and reset() is skipped
  // on a launched exit — so after launching drip A the slice still holds A's
  // hydrated content. Treat "hydrated for a DIFFERENT drip" as not-ready so we
  // never render or autosave A's steps onto route B before B re-hydrates.
  const hydratedForThisDrip = hydrated && sliceDripId === dripId;
  const [submitting, setSubmitting] = React.useState(false);
  // Mobile: the header stepper (+ fixed 220px name input) overflows a phone;
  // hide the stepper (the footer already shows "Step N of M") and let the name
  // input fill the width.
  const isMobile = useIsMobile();
  // What went wrong with the last launch attempt, rendered in the SAME slot as
  // the unknown-token warning above the Launch button (the wizard's existing way
  // of telling the user why the button won't do what they expect). Before this,
  // a failed launch only reached `console.error` — the user saw a button that
  // silently re-enabled, or (on a hung request) one that never did.
  const [launchError, setLaunchError] = React.useState<string | null>(null);
  // "Send now" reaches every contact in the audience the moment it is pressed,
  // with no undo. The button therefore says how many people that is, and an
  // immediate send asks once before it goes out. (A SCHEDULED campaign is not
  // gated: it can still be edited or stopped before its time.)
  const [pendingLaunchConfirm, setPendingLaunchConfirm] = React.useState(false);
  // Set once the user launches/schedules: the draft becomes a live campaign, so
  // the exit-deletion cleanup must NEVER reclaim it even though leaving the
  // wizard (navigate to /launched) unmounts this component.
  const launchedRef = React.useRef(false);

  // Hydrate the slice once the draft arrives and the slice isn't already
  // hydrated for THIS drip (re-hydrates if the route's dripId changes).
  React.useEffect(() => {
    if (!draft) return;
    if (hydrated && sliceDripId === dripId) return;
    dispatch(hydrate({
      dripId,
      steps: hydrateSteps(draft),
      name: draft.name ?? '',
      // `?? false` mirrors the backend, which exits only on stopOnReply === true.
      stopOnReply: draft.stopOnReply ?? false,
      timing: hydrateTiming(draft),
    }));
  }, [dispatch, draft, hydrated, sliceDripId, dripId]);

  // Clear the working copy when leaving the wizard — but NEVER for a launched
  // draft (it's now a live campaign).
  React.useEffect(() => () => {
    if (!launchedRef.current) dispatch(reset());
  }, [dispatch]);

  // ── Audience selection — SoT is the draft's boardIDArray (persisted) ──
  // A drip targets N audiences: the backend resolver (calculateCampaignMetaData,
  // reached via drip-orchestrator) already loops every board in boardIDArray and
  // dedupes recipients by whatsappId across them, so the wizard carries the whole
  // set rather than the first entry (#161).
  const audienceIds = React.useMemo(() => toAudienceIds(draft?.boardIDArray), [draft?.boardIDArray]);
  // Stable primitive dep for the per-board fetch effects below — a fresh array
  // identity each render would re-fire them forever.
  const audienceIdsKey = audienceIds.join(',');

  // ── Autosave (debounced patch) whenever the working copy changes ──
  const patchDraft = React.useCallback((changes: Partial<Drip>) => {
    dispatch(DBDripThunks.patch({ entity: { _id: dripId, ...changes } }));
  }, [dispatch, dripId]);

  // Debounced content autosave (steps / name / stopOnReply / timing).
  const saveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    if (!hydratedForThisDrip) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      patchDraft({
        name: name.trim() || '',
        steps: buildSteps(steps),
        stopOnReply,
        schedule: buildSchedule(timing),
        randomGap: buildRandomGap(timing),
      });
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [hydratedForThisDrip, steps, name, stopOnReply, timing, patchDraft]);

  // Audience selection autosaves immediately (it's a discrete choice, not typing).
  const selectAudiences = React.useCallback((boardIds: string[]) => {
    patchDraft({ boardIDArray: boardIds });
  }, [patchDraft]);

  // ── REAL audiences: boards of type 'audience' + contact counts ──
  const audiences = useAppSelector((state): AudienceOption[] => {
    const boards = boardsSelectByQuery(
      state.DBBoardsReducer,
      {
        deleted: { $ne: true },
        type: 'audience',
      },
      ['_id', 'name'],
      { order: 1 },
    );
    const counts = getTaskSumByBoardIdRecord(state);
    return boards
      .filter((b): b is typeof b & { _id: string } => typeof b._id === 'string')
      .map((b, i) => ({
        id: b._id,
        name: b.name ?? t('audiences.unnamed', 'Untitled audience'),
        count: counts[b._id] ?? 0,
        color: tintForIndex(i),
      }));
  });

  // Preserve the user's tick order (audiences[] is name-ordered), and keep only
  // boards we could actually resolve — an id with no board yet simply hasn't been
  // fetched, and rendering a placeholder card for it would be noise.
  const selectedAudiences = React.useMemo(
    () => audienceIds.map((id) => audiences.find((a) => a.id === id)).filter((a): a is AudienceOption => Boolean(a)),
    [audienceIds, audiences],
  );
  // How many people "Send now" actually reaches — the number the button and the
  // confirmation both quote. (A contact in two audiences is counted twice here,
  // exactly as the review step counts them.)
  const recipientTotal = React.useMemo(
    () => selectedAudiences.reduce((n, a) => n + a.count, 0),
    [selectedAudiences],
  );

  const selectedAudienceLabel = React.useMemo(
    () => deriveAudienceLabel(selectedAudiences.map((a) => a.name), t),
    [selectedAudiences, t],
  );
  const workingSteps = steps;

  // ── Keep the selected audience's name + contact count present AND fresh ──
  // The mount-time `find({ type:'audience' })` is capped/paginated, so the chosen
  // board can be absent from the store (→ no name/count on the review). And
  // audience boards carry NO denormalised `tasksCount`, so the count is derived
  // from LOADED tasks (getTaskSumByBoardIdRecord's fallback). Fetching the board
  // by id + its contacts makes the wizard resolve the board AND reflect the live
  // count. `$paginate:false` loads the whole board so the count is exact.
  //
  // Freshness after an EXTERNAL edit: the user typically edits the audience's
  // contacts on a SEPARATE tab/route (a separate Redux store), so simply
  // switching BACK to this already-mounted wizard tab wouldn't re-fetch and the
  // count would stay stale until a full page reload ("only if i refreshed"). So
  // we also re-fetch whenever the tab regains visibility/focus — returning to the
  // tab then acts like a refresh for the count.
  React.useEffect(() => {
    const ids = audienceIdsKey ? audienceIdsKey.split(',') : [];
    if (ids.length === 0) return;
    const refetch = () => {
      for (const boardId of ids) {
        dispatch(DBBoardsThunks.find({ _id: boardId }));
        dispatch(DBTasksThunks.find({ boardId, deleted: false, archived: { $ne: true }, $paginate: false }));
      }
    };
    refetch();
    const onVisible = () => { if (document.visibilityState === 'visible') refetch(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refetch);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refetch);
    };
  }, [dispatch, audienceIdsKey]);

  // ── Personalization-token validation ────────────────────────────────────────
  // A {token} resolves at send against the chosen audience board's task columns
  // (plus the canonical contact fields — see bt-common personalization-tokens).
  // Load those columns and flag any token that won't resolve, so a campaign with
  // an unresolvable token (e.g. a typo, or the {{double-brace}} that shipped
  // literally) can't be launched.
  React.useEffect(() => {
    for (const boardId of audienceIdsKey ? audienceIdsKey.split(',') : []) {
      dispatch(DBTaskColumnThunks.find({ boardId, deleted: { $ne: true } }));
    }
  }, [dispatch, audienceIdsKey]);

  // With several audiences a token has to resolve for EVERY one of them — a
  // custom column that exists only on some boards would ship literally to the
  // recipients coming from the others. So the extra titles handed to the
  // validator are the INTERSECTION across the selected boards, not the union.
  // (Canonical contact fields — {name}, {phone}, … — are known to the validator
  // itself and don't depend on this list at all.)
  const audienceColumnTitles = useAppSelector((s): string[] => {
    const ids = audienceIdsKey ? audienceIdsKey.split(',') : [];
    if (ids.length === 0) return [];
    return intersectColumnTitles(
      ids.map((boardId) =>
        taskColumnSelectByQuery(
          s.DBTaskColumnReducer,
          { boardId, deleted: { $ne: true } },
          ['title', 'type'],
        )
          .map((c) => c.title)
          .filter((t): t is string => typeof t === 'string'),
      ),
    );
  });

  const unknownTokens = React.useMemo<string[]>(() => {
    const found = new Set<string>();
    for (const s of workingSteps) {
      const message = s.template?.message;
      if (typeof message === 'string') {
        // Collapse `{{x}}`→`{x}` first (the backend resolves double-brace too),
        // so a Handlebars-habit token isn't wrongly flagged as unknown.
        for (const tok of findUnknownTokens(collapseDoubleBraces(message), audienceColumnTitles)) found.add(tok);
      }
    }
    return [...found];
  }, [workingSteps, audienceColumnTitles]);

  // ── Empty-draft cleanup on wizard EXIT ──────────────────────────────────
  // The persisted draft was created up front by /campaigns/new; if the user
  // leaves the wizard without giving it an audience or any real message
  // content, it's an empty throwaway that would otherwise litter the list as a
  // "No audience / Draft" card. We delete it when this component unmounts —
  // "exit" is therefore EVERY way of leaving the wizard route: the header X,
  // the footer "Exit"/"Save draft" buttons (all `navigate(flowPath('/campaigns'))`),
  // browser Back, or any route-away. Because the shell keys <CampaignWizard> by
  // dripId, that unmount fires reliably.
  //
  // GUARDS (read via a ref so the unmount cleanup sees the LATEST values, not
  // the values captured when the effect first ran):
  //   1. NOT hydrated yet (`!hydrated`) → skip. The draft is mid-flight from
  //      /campaigns/new (StrictMode double-mount, or the brief gap before the
  //      first `find` resolves). Deleting here would race the redirect and
  //      destroy a draft the user is about to edit — so a never-hydrated wizard
  //      never deletes.
  //   2. Already launched (`launchedRef`) → skip. The draft is now a live
  //      campaign; navigating to /launched must not reclaim it.
  //   3. Has content → skip. Emptiness is judged from the LIVE working copy
  //      (the chosen audiences + the in-memory steps), not the persisted draft, so a draft
  //      with edits typed within the autosave-debounce window is correctly kept
  //      even though the debounced patch may not have flushed yet. Mirrors the
  //      list's `isEmptyDraft` exactly (DRY).
  const cleanupStateRef = React.useRef({ shouldDelete: false });
  cleanupStateRef.current.shouldDelete =
    hydratedForThisDrip &&
    !launchedRef.current &&
    isEmptyDraft({ status: 'draft', boardIDArray: audienceIds, steps: buildSteps(workingSteps) });

  React.useEffect(() => {
    return () => {
      // GUARD 4: a DETOUR into a board/template sub-editor is a round-trip, not
      // an exit — the user returns via Back. Never reclaim the draft they're
      // about to come back to (else Back lands on a deleted draft that re-fetches
      // nothing → permanent "Loading…"). The shell recorded the destination path.
      if (isNavigatingToSubEditor()) return;
      if (cleanupStateRef.current.shouldDelete) {
        dispatch(DBDripThunks.delete({ entity: { _id: dripId } }));
      }
    };
  }, [dispatch, dripId]);
  const multi = workingSteps.length > 1;
  const idx = STEPS.findIndex((s) => s.id === step);
  const maxReached = Math.max(idx, audienceIds.length > 0 ? 1 : 0);

  // ── Which follow-up editor is open — owned by the URL (?step=<n>) ──
  const editingIdx = React.useMemo<number | null>(() => {
    const raw = searchParams.get('step');
    if (raw == null) return null;
    const n = Number.parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0 || n >= workingSteps.length) return null;
    return n;
  }, [searchParams, workingSteps.length]);

  const setEditingIdx = React.useCallback((next: number | null) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next == null) p.delete('step');
      else p.set('step', String(next));
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  // ── Navigation — every step transition is a route change ──
  // `replace`: the whole create/edit flow is ONE history slot. Stepping between
  // wizard steps (via the footer Back/Continue) must not pile entries onto the
  // browser back-stack, so that after launch the launched screen + wizard don't
  // sit behind the detail (the detail's Back would otherwise walk back through
  // the entire wizard instead of returning to the list). In-step back is the
  // wizard's own footer button (go(-1)); browser-back exits the wizard.
  const goToStep = React.useCallback((id: WizardStepId) => {
    navigate(`/campaigns/${dripId}/edit/${id}`, { replace: true });
  }, [navigate, dripId]);

  const onExit = React.useCallback(() => {
    navigate(flowPath('/campaigns'));
  }, [navigate]);

  // Every step must carry content (message/media/poll) — an empty follow-up
  // would fire and send nothing. Gate BOTH the message-step Continue and the
  // launch itself on this (the Continue gate alone is bypassable via back-nav
  // or a pre-existing draft, so an empty step could otherwise reach launch).
  const allStepsHaveContent = workingSteps.length > 0 && workingSteps.every((s) => hasContent(s.template));

  // An attachment is uploaded ASYNCHRONOUSLY: `MessageEditorAttachmentBox` flips
  // this flag while the signed PUT is in flight and only writes the asset onto
  // the step's template once the upload LANDS. Continue during that window and
  // the step advances with no asset — the campaign quietly goes out as text-only
  // while the composer showed a picture. Same gate the older surfaces already
  // apply (`selectCanContinueToNextTab`, the scheduled-message dialog footer).
  const isFileUploading = useAppSelector((s) => s.ScheduledMessageEditorReducer.isFileUploading);

  const canNext = (() => {
    if (step === 'audience') return audienceIds.length > 0;
    if (step === 'message') return allStepsHaveContent && !isFileUploading;
    // BE#657 B1 — a SCHEDULED launch must carry a user-picked time. The date/time
    // picker no longer self-seeds "now" (dueDateSetByUser gate), so without this a
    // schedule with an untouched picker would advance to Review with no dueDate and
    // the backend would default it to new Date() → a silent immediate launch.
    if (step === 'timing') return !scheduledLaunchNeedsTime(timing);
    return true;
  })();

  const go = (dir: number) => {
    const ni = idx + dir;
    if (ni < 0) {
      if (dir < 0) onExit();
      return;
    }
    if (ni >= STEPS.length) return;
    goToStep(STEPS[ni].id);
  };

  const requestLaunch = () => {
    if (submitting || audienceIds.length === 0 || !hydratedForThisDrip) return;
    // BE#657 B1 — refuse a scheduled launch with no picked time (would otherwise
    // fire immediately: buildSchedule sends isTimeValid:true with no dueDate and the
    // drip orchestrator defaults a missing dueDate to now). Surface it + jump to Timing.
    if (scheduledLaunchNeedsTime(timing)) {
        setLaunchError(t('campaignsRedesign.wizard.pickTimeBeforeLaunch', 'Pick a send time before scheduling the campaign.'));
        goToStep('timing');
        return;
    }
    // Only an immediate send is irreversible; a scheduled one still has a
    // window in which it can be changed or stopped.
    if (!isEditingLaunched && timing.launch === 'now') {
        setPendingLaunchConfirm(true);
        return;
    }
    void handleLaunch();
  };

  const handleLaunch = async () => {
    if (submitting || audienceIds.length === 0 || !hydratedForThisDrip) return;
    // Any press clears the previous attempt's message — including the presses
    // that only bounce to another step, so the user never comes back to `review`
    // and reads a stale failure from a launch they have since acted on.
    setLaunchError(null);
    // Guard: never launch an empty step (would send nothing) — send them back to fix it.
    if (!allStepsHaveContent) { goToStep('message'); return; }
    // Guard: never launch with a token that won't resolve at send.
    if (unknownTokens.length > 0) { goToStep('review'); return; }
    // GUARD OF RECORD: never launch a drip that is ALREADY launched. `launchDrip`
    // mints a fresh step-0 campaign over the whole audience on every call, so a
    // second press messages every recipient twice. `menuEditTargetFor` keeps the
    // menu off this route, but the route itself (`/campaigns/:id/edit/review`)
    // is reachable by typed URL, stale tab or browser history — and doing so on
    // a live drip really did mint a duplicate step-0 campaign. The guard must sit
    // BEFORE the patch below: that patch used to overwrite `status:'draft'`,
    // destroying the very evidence this reads (and lying to the server's own
    // `status ∈ {draft,null}` guard, which is why the backend never refused).
    // Read from `draft` — the persisted drip from the store — not from local
    // state, which knows only about content.
    if (isEditingLaunched) {
      // FE#332 — EDIT-IN-PLACE. The drip is already launched, so we SAVE the
      // authored changes back to the SAME drip and NEVER call launchDrip/
      // advanceDrip — either would mint a second step-0 campaign over the whole
      // audience (the double-send). `buildDripEditPatch` omits status/currentStep
      // (never re-draft or rewind a live drip); the backend
      // `syncPendingStepCampaignOnPatch` hook fans the edit onto the PENDING step
      // campaigns and is guarded to skip any running/complete step.
      setSubmitting(true);
      launchedRef.current = true; // a live drip must never be reclaimed on exit
      try {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        await dispatch(DBDripThunks.patch({
          entity: buildDripEditPatch({
            dripId,
            name: name.trim() || deriveCampaignName(steps, t, selectedAudienceLabel),
            boardIDArray: audienceIds,
            steps: buildSteps(steps),
            stopOnReply,
            schedule: buildSchedule(timing),
            randomGap: buildRandomGap(timing),
          }),
        })).unwrap();
        navigate(`/campaigns/${dripId}`, { replace: true });
      } catch (err) {
        console.error('[CampaignWizard] edit save failed', err);
        launchedRef.current = false; // save failed — allow another attempt
        setSubmitting(false);
        setLaunchError(t('campaignsRedesign.wizard.saveFailed', 'We could not save your changes. Please try again.'));
      }
      return;
    }
    setSubmitting(true);
    // Mark launched BEFORE the await so the exit-deletion cleanup can never
    // reclaim this draft once we've committed to launching (the navigate to
    // /launched that follows will unmount the wizard).
    launchedRef.current = true;
    // BOUNDED. `submitting` used to be reset ONLY in `catch`, and a request that
    // never settles never reaches `catch` — a slow backend therefore left the
    // Launch button `disabled` forever with no error and no recovery but a page
    // reload (observed live: five stacked pending PATCHes to /drips/<id>).
    // `resolveLaunchOutcome` stops waiting after LAUNCH_TIMEOUT_MS and reports
    // WHY, and `decideLaunchOutcome` owns what that means — including the 409
    // that tells us a timed-out launch actually landed. See launch-outcome.ts.
    const launchSequence = (async () => {
      // Flush the final working copy synchronously (don't race the debounce),
      // then launch.
      //
      // NOTE: this deliberately does NOT send `status`. It used to write
      // `status:'draft'` unconditionally, which was a lie about the drip's state
      // on every path where the drip was not in fact a draft — and it is the
      // reason the server's own `status ∈ {draft,null}` launch guard could never
      // refuse: the client re-drafted the drip a moment before asking to launch
      // it. On the genuine first launch the drip is ALREADY `draft` (the draft
      // creator writes it at create time), so omitting the field is a no-op
      // there; everywhere else omitting it is the whole point. The client no
      // longer overwrites state it does not own.
      if (saveTimer.current) clearTimeout(saveTimer.current);
      await dispatch(DBDripThunks.patch({
        entity: {
          _id: dripId,
          name: name.trim() || deriveCampaignName(steps, t, selectedAudienceLabel),
          boardIDArray: audienceIds,
          steps: buildSteps(steps),
          stopOnReply,
          currentStep: 0,
          schedule: buildSchedule(timing),
          randomGap: buildRandomGap(timing),
        },
      })).unwrap();
      await launchDrip(dripId).unwrap();
    })().catch((err) => {
      // Log the real error before it is classified away — the outcome alone is
      // not enough to debug from.
      console.error('[CampaignWizard] launch failed', err);
      throw err;
    });

    const outcome = await resolveLaunchOutcome(launchSequence, LAUNCH_TIMEOUT_MS);
    if (outcome === 'timeout') {
      console.warn(`[CampaignWizard] launch not confirmed within ${LAUNCH_TIMEOUT_MS}ms — offering a retry`);
    }
    const decision = decideLaunchOutcome(outcome, dripId);
    // The drip is a launchable draft again only on a DEFINITE failure — never
    // after a timeout, where we cannot know whether the launch landed and the
    // exit cleanup must not be pointed at what may be a live campaign.
    if (!decision.keepLaunchedFlag) launchedRef.current = false;
    if (decision.resetSubmitting) setSubmitting(false);
    setLaunchError(decision.errorMessage ?? null);
    // replace: the wizard is now done — don't leave it behind the launched
    // screen (so its Back / Track-progress land on the list, not the wizard).
    if (decision.navigateTo) navigate(decision.navigateTo, { replace: true });
  };

  // Lift the Crisp bubble above the wizard footer so it never covers the
  // Continue/Launch button (rule lives in campaigns-redesign.css; Crisp mounts
  // on document.body, outside any shadow root, so a body class reaches it).
  React.useEffect(() => {
    document.body.classList.add('bt-wizard-crisp-raised');
    return () => document.body.classList.remove('bt-wizard-crisp-raised');
  }, []);

  // While the draft is still loading, render the chrome but a quiet body.
  const loading = !hydratedForThisDrip;

  return (
    <div className="col" style={{ flex: 1, background: 'var(--bg)' }}>
      {/* On mobile the app shell drops a FIXED nav burger at top-left (app-view.tsx,
          `!showHeader` branch: position:fixed, insetInlineStart:8, z-index:1100). Pad the
          wizard header's leading edge so its name/title clears that burger instead of being
          overlapped by it. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', paddingInlineStart: isMobile ? 56 : 20, borderBottom: '1px solid var(--line)', background: 'var(--surface)' }}>
        {/* Balanced 3-column header: equal-flex left/right flanks keep the stepper
            on the TRUE header centre (a single flex:1 center block would centre it
            only in the leftover space, biasing it right since the flanks differ
            in width). */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
          <input
            value={name}
            onChange={(e) => dispatch(setNameAction(e.target.value))}
            placeholder={deriveCampaignName(workingSteps, t, selectedAudienceLabel)}
            className="inp"
            // A NAME field is single-line by definition, so the sidebar's answer
            // (let it wrap) is not available here — an <input> cannot wrap and a
            // two-line name field would be a worse control. The fix is the other
            // half of that same defect: the clip had no ELLIPSIS, so the name just
            // stopped mid-word with nothing saying there was more. `text-overflow`
            // supplies it (Chromium applies it to the placeholder too), the derived
            // placeholder now breaks at a word, and the fixed 220px grows into the
            // header flank it was already sitting in — three ways of giving the
            // same sentence room, none of them a second line.
            style={{
              width: '100%', maxWidth: isMobile ? undefined : 360, minWidth: 0,
              background: 'transparent', border: '1px solid transparent',
              fontWeight: 600, fontSize: 14, padding: '5px 8px',
              textOverflow: 'ellipsis',
            }}
            onFocus={(e) => { e.target.style.borderColor = 'var(--line)'; }}
            onBlur={(e) => { e.target.style.borderColor = 'transparent'; }}
          />
        </div>
        {!isMobile && <WizardStepper active={step} maxReached={maxReached} onJump={goToStep} multi={multi} stepCount={workingSteps.length} />}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
          <button type="button" className="btn sm" onClick={onExit}>{t('campaignsRedesign.wizard.saveDraft', 'Save draft')}</button>
          <div style={{ width: 1, height: 24, background: 'var(--line)' }} />
          <button type="button" className="btn ghost icon" onClick={onExit} aria-label={t('campaignsRedesign.wizard.exit', 'Exit')}><X size={16} /></button>
        </div>
      </div>

      <div className="scroll" style={{ flex: 1, padding: '28px 24px 24px' }}>
        {loading ? (
          <div className="fade-in" style={{ maxWidth: 720, margin: '40px auto', textAlign: 'center', fontSize: 13, color: 'var(--muted)' }}>{t('campaignsRedesign.wizard.loading', 'Loading…')}</div>
        ) : (
          <>
            {step === 'audience' && (
              <StepNotice show={isActivelySending} note={t('campaignsRedesign.wizard.noticeAudienceSending', 'This campaign is already sending. A change here applies to the messages still to go out — the one sending right now keeps the audience it started with.')}>
                <DripAudienceStep selected={audienceIds} onSelect={selectAudiences} />
              </StepNotice>
            )}
            {step === 'message' && <MessageStep steps={workingSteps} stopOnReply={stopOnReply} setStopOnReply={(v) => dispatch(setStopOnReplyAction(v))} editingIdx={editingIdx} setEditingIdx={setEditingIdx} />}
            {step === 'timing' && (
              <StepNotice show={isActivelySending} locked note={t('campaignsRedesign.wizard.noticeTimingSending', 'This campaign is already sending. The time for the message going out right now can no longer be changed.')}>
                <TimingStep timing={timing} setTiming={(updater) => dispatch(setTimingAction(updater(timing)))} multi={multi} steps={workingSteps} totalContacts={selectedAudiences.reduce((n, a) => n + a.count, 0)} />
              </StepNotice>
            )}
            {step === 'review' && <FinishStep steps={workingSteps} stopOnReply={stopOnReply} timing={timing} audiences={selectedAudiences} multi={multi} onJump={goToStep} />}
          </>
        )}
      </div>

      <div style={{ padding: '12px 24px', borderTop: '1px solid var(--line)', background: 'var(--surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', boxSizing: 'border-box' }}>
          <button type="button" className="btn" onClick={() => go(-1)}>
            <ChevronLeft size={14} /> {idx === 0 ? t('campaignsRedesign.wizard.exit', 'Exit') : t('common.back', 'Back')}
          </button>
          <div style={{ flex: 1, textAlign: 'center', fontSize: 11.5, color: 'var(--muted)' }}>
            {t('campaignsRedesign.wizard.stepCounter', 'Step {{current}} of {{total}}', { current: idx + 1, total: STEPS.length })}
            {step === 'message' && (
              <span style={{ marginInlineStart: 8, color: multi ? 'var(--bt-blue-deep)' : 'var(--muted-2)' }}>
                · {multi
                  ? t('campaignsRedesign.counts.messages', { count: workingSteps.length })
                  : t('campaignsRedesign.wizard.singleMessageAside', 'one message')}
              </span>
            )}
          </div>
          {step === 'review' ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              {unknownTokens.length > 0 && (
                <span style={{ fontSize: 11, color: 'var(--danger, #d33)', maxWidth: 320, textAlign: 'end' }}>
                  {audienceIds.length > 1
                    ? t(
                      'campaignsRedesign.wizard.unknownTokensMultiAudience',
                      '{{tokens}} won’t work — not every audience you picked has a column with that name.',
                      { tokens: unknownTokens.map((tok) => `{${tok}}`).join(', ') },
                    )
                    : t(
                      'campaignsRedesign.wizard.unknownTokens',
                      '{{tokens}} won’t work — this audience has no column with that name.',
                      { tokens: unknownTokens.map((tok) => `{${tok}}`).join(', ') },
                    )}
                </span>
              )}
              {/* Launch failure/timeout — same slot, same treatment as the token
                  warning above: this is the wizard's one place for "why the
                  button did not do what you expected". Cleared on the next press. */}
              {launchError && (
                <span role="alert" style={{ fontSize: 11, color: 'var(--danger, #d33)', maxWidth: 360, textAlign: 'end' }}>
                  {launchError}
                </span>
              )}
              <button
                type="button"
                className="btn primary"
                disabled={submitting || loading || unknownTokens.length > 0 || !allStepsHaveContent}
                onClick={requestLaunch}
              >
                {isEditingLaunched
                  ? (<><Send size={14} /> {t('common.saveChanges', 'Save changes')}</>)
                  : timing.launch === 'now'
                    ? (<><Send size={14} /> {recipientTotal > 0
                      ? t('campaignsRedesign.wizard.sendNowToContacts', 'Send now to {{n}} contacts', { count: recipientTotal, n: recipientTotal.toLocaleString() })
                      : t('campaignsRedesign.wizard.sendNow', 'Send now')}</>)
                    : (<><Send size={14} /> {t('campaignsRedesign.wizard.scheduleCampaign', 'Schedule it')}</>)}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn primary"
              disabled={!canNext || loading}
              // A disabled button with no reason reads as broken. The upload is the
              // one case here that resolves on its own, so it gets said out loud.
              onClick={() => go(1)}
            >
              {step === 'message' && isFileUploading
                ? (<>{t('campaignsRedesign.wizard.uploading', 'Uploading…')} <ChevronRight size={14} /></>)
                : (<>{t('campaignsRedesign.wizard.continue', 'Continue')} <ChevronRight size={14} /></>)}
            </button>
          )}
        </div>
      </div>

      {/* Send-now is the one irreversible step in the wizard: it reaches the
          whole audience the moment it is confirmed, and nothing recalls it. */}
      {pendingLaunchConfirm && (
        <MessageConfirmationDialog
          title={recipientTotal > 0
            ? t('campaignsRedesign.wizard.confirmSendTitle', 'Send to {{count}} contacts now?', { count: recipientTotal })
            : t('campaignsRedesign.wizard.confirmSendTitleNoCount', 'Send this campaign now?')}
          description={t('campaignsRedesign.wizard.confirmSendBody', 'Messages start going out immediately. They cannot be recalled once sent.')}
          confirmLabel={t('campaignsRedesign.wizard.confirmSendCta', 'Yes, send now')}
          onClose={(value?: string) => {
            setPendingLaunchConfirm(false);
            if (value !== undefined) void handleLaunch();
          }}
        />
      )}
    </div>
  );
};
